
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { EditorModeToggle } from "./EditorModeToggle";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { 
  refreshEnvironmentDetection, 
  checkEnvironmentDetectionConsistency,
  storeEditorDetection,
  isLovableEditor
} from "@/utils/environment";

export function EditorModeTab() {
  const [diagnosticDetails, setDiagnosticDetails] = React.useState<Record<string, any>>({});
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [isFloatingToggleVisible, setIsFloatingToggleVisible] = React.useState(
    localStorage.getItem('lamulamu_diagnostic_toggle_visible') === 'true'
  );

  const refreshDetection = () => {
    const result = refreshEnvironmentDetection();
    checkEnvironmentDetectionConsistency();
    
    // Update diagnostic information
    setDiagnosticDetails({
      detectionResult: result,
      inIframe: isInIframe(),
      urlContainsLovable: window.location.href.includes('lovable'),
      documentHasEditorClass: document.documentElement.classList.contains('lovable-editor'),
      timestamp: new Date().toISOString(),
    });
  };

  const isInIframe = () => {
    try {
      return window.self !== window.top;
    } catch (e) {
      return true; // If we get an error, we're in an iframe
    }
  };
  
  const toggleFloatingVisibility = () => {
    const newValue = !isFloatingToggleVisible;
    setIsFloatingToggleVisible(newValue);
    localStorage.setItem('lamulamu_diagnostic_toggle_visible', newValue ? 'true' : 'false');
    
    // Also reset the user preference when admin toggles this
    if (!newValue) {
      // If admin is disabling the toggle, also clear the user preference
      sessionStorage.removeItem('lamulamu_diagnostic_toggle_user_visible');
    }
    
    console.log(`[EditorModeTab] Floating toggle admin setting: ${newValue ? 'enabled' : 'disabled'}`);
  };

  React.useEffect(() => {
    refreshDetection();
  }, []);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Editor Mode Settings</CardTitle>
          <CardDescription>
            Manage how the application detects and behaves in the Lovable Editor environment
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium">Editor Mode</h3>
                  <p className="text-sm text-muted-foreground">
                    Toggle between Lovable Editor and standard mode
                  </p>
                </div>
                <EditorModeToggle />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium">Floating Toggle</h3>
                  <p className="text-sm text-muted-foreground">
                    Show/hide the floating Editor Mode toggle in the bottom left
                  </p>
                </div>
                <Switch 
                  checked={isFloatingToggleVisible}
                  onCheckedChange={toggleFloatingVisibility}
                  id="floating-toggle"
                />
              </div>
            </div>

            <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-md">
              <h4 className="text-md font-medium mb-3">Keyboard Shortcuts</h4>
              <div className="space-y-3">
                <div>
                  <h5 className="font-medium text-sm">Show/Hide Floating Toggle</h5>
                  <div className="flex flex-col gap-1 mt-1">
                    <p className="text-sm">
                      <span className="font-medium">Windows/Linux:</span> <kbd className="px-2 py-0.5 rounded bg-gray-200 dark:bg-gray-700">Alt</kbd> + <kbd className="px-2 py-0.5 rounded bg-gray-200 dark:bg-gray-700">Shift</kbd> + <kbd className="px-2 py-0.5 rounded bg-gray-200 dark:bg-gray-700">D</kbd>
                    </p>
                    <p className="text-sm">
                      <span className="font-medium">Mac:</span> <kbd className="px-2 py-0.5 rounded bg-gray-200 dark:bg-gray-700">Option</kbd> + <kbd className="px-2 py-0.5 rounded bg-gray-200 dark:bg-gray-700">Shift</kbd> + <kbd className="px-2 py-0.5 rounded bg-gray-200 dark:bg-gray-700">D</kbd>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mt-4">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={refreshDetection}
              >
                Refresh Detection
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => {
                  checkEnvironmentDetectionConsistency();
                  refreshDetection();
                }}
              >
                Verify Consistency
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setIsExpanded(!isExpanded)}
              >
                {isExpanded ? "Hide" : "Show"} Diagnostic Details
              </Button>
            </div>

            {isExpanded && (
              <div className="mt-4 p-4 bg-muted rounded-md overflow-auto max-h-80">
                <pre className="text-xs">{JSON.stringify(diagnosticDetails, null, 2)}</pre>
                <div className="mt-2">
                  <h4 className="text-sm font-medium mb-1">Storage State:</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="text-xs">
                      <span className="font-medium">Session Storage:</span>{" "}
                      {sessionStorage.getItem('lamulamu_is_lovable_editor')}
                    </div>
                    <div className="text-xs">
                      <span className="font-medium">Local Storage:</span>{" "}
                      {localStorage.getItem('lamulamu_is_lovable_editor')}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
